import { useNavigate } from 'react-router-dom';
import { useFlag, usePass } from '../../api/hooks';
import { haptics } from '../../telegram';
import { track } from '../../analytics';
import { lastShownLevel, markShownLevel, seasonTimer, toNextLabel } from './level';
import { plural } from '../../format';
import { useEffect, useRef, useState } from 'react';

/**
 * 6.0a (Handoff 3) — карточка Straddle Pass на главной, одна строка:
 * слева лейбл и уровень, в центре прогресс и чипы, справа шеврон.
 * Тап по карточке → 6.1; чип «Pass+ …» без пасса → 6.2a.
 */
export function PassHomeCard() {
  const enabled = useFlag('pass');
  const pass = usePass(enabled);
  const navigate = useNavigate();
  // 6.0a (handoff 4 §2.3): при выросшем уровне — flip числа один раз,
  // без haptic и тоста; экран записывает lastShownLevel (§2.4).
  const [flip, setFlip] = useState(false);
  const seen = useRef(false);
  // Первый блик — со случайной задержкой 0–1,8 с (handoff 3 §5)
  const shineDelay = useRef(+(Math.random() * 1.8).toFixed(2));
  useEffect(() => {
    const m = pass.data?.me;
    if (!m || seen.current || pass.data?.status !== 'active') return;
    seen.current = true;
    const prev = lastShownLevel();
    if (
      prev &&
      m.level > prev.level &&
      !(typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches)
    )
      setFlip(true);
    markShownLevel(m.level, m.xp);
  }, [pass.data]);
  if (!enabled || !pass.data || pass.data.status !== 'active') return null;
  const { season, me, tracks } = pass.data;
  if (!season || !me) return null;

  const plus = me.tier === 'plus';
  const endingSoon = season.daysLeft < 1;
  const nextRewardLevel =
    (tracks ?? []).find((r) => r.level > me.level && ((plus && r.plus) || r.free))?.level ?? null;

  return (
    <div
      className="bp-home-card bp-home-card--row"
      data-theme="dark"
      role="button"
      onClick={() => {
        haptics.tap();
        track('pass_home_card_tap', {});
        navigate('/pass');
      }}
    >
      <span className="bp-home-shine" style={{ animationDelay: `${shineDelay.current}s` }} aria-hidden />
      <div className="bp-home-left">
        <span className="bp-home-label">{plus ? 'STRADDLE PASS+' : 'STRADDLE PASS'}</span>
        <span className="bp-home-lvlrow">
          <b className={`mono${flip ? ' bp-home-flip' : ''}`}>{me.level}</b>
          <span>ур.</span>
        </span>
      </div>
      <div className="bp-home-center">
        <div className="bp-home-progress-label">
          <span>
            {me.level < season.levels
              ? `${toNextLabel(me.level + 1, me.xp, season.xpPerGame)} до ${me.level + 1}`
              : 'максимальный уровень'}
          </span>
          <span className={`tm mono${endingSoon ? ' warn' : ''}`}>{seasonTimer(season.endsAt)}</span>
        </div>
        <div className="bp-home-bar">
          <i style={{ width: `${Math.round(me.xpFrac * 100)}%` }} />
        </div>
        <div className="bp-home-chips">
          {!plus ? (
            <span
              className="bp-chip bp-chip--plus"
              onClick={(e) => {
                e.stopPropagation();
                haptics.tap();
                navigate('/pass/plus');
              }}
            >
              Pass+ · награда на каждом уровне
            </span>
          ) : endingSoon && me.decksUnopened > 0 ? (
            <span className="bp-chip bp-chip--warn">Открой колоды до конца сезона</span>
          ) : me.claimable === 0 && me.decksUnopened === 0 ? (
            nextRewardLevel !== null && (
              <span className="bp-home-nextnote">Следующая награда на ур. {nextRewardLevel}</span>
            )
          ) : (
            <>
              {me.claimable > 0 && (
                <span className="bp-chip bp-chip--gold bp-chip--pop">
                  <i className="dot" aria-hidden />
                  {me.claimable} {plural(me.claimable, ['награда ждёт', 'награды ждут', 'наград ждут'])}
                </span>
              )}
              {me.decksUnopened > 0 && (
                <span className="bp-chip">
                  <svg width="8" height="11" viewBox="0 0 24 30" fill="rgba(124,107,153,.3)" stroke="#9b8ab5" strokeWidth="2.6" aria-hidden style={{ marginRight: 5 }}>
                    <rect x="2" y="2" width="20" height="26" rx="4" />
                  </svg>
                  {me.decksUnopened}{' '}
                  {me.decksUnopened === 1 ? 'колода' : me.decksUnopened < 5 ? 'колоды' : 'колод'}
                </span>
              )}
            </>
          )}
        </div>
      </div>
      <span className="bp-home-chev" aria-hidden>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m9 5 7 7-7 7" />
        </svg>
      </span>
    </div>
  );
}

/** Кнопка «Магазин» под карточкой пасса на главной → магазин колод. */
export function ShopHomeButton() {
  const enabled = useFlag('pass');
  const pass = usePass(enabled);
  const navigate = useNavigate();
  if (!enabled || pass.data?.status !== 'active') return null;
  return (
    <button
      className="bp-home-shop"
      onClick={() => {
        haptics.tap();
        track('pass_shop_open', { from: 'home' });
        navigate('/pass/decks?shop=1');
      }}
    >
      <span className="ic" aria-hidden>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 8h14l-1.2 11.2a1.8 1.8 0 0 1-1.8 1.6H8a1.8 1.8 0 0 1-1.8-1.6z" />
          <path d="M8.6 8V6.4a3.4 3.4 0 0 1 6.8 0V8" />
        </svg>
      </span>
      <span className="nm">Магазин</span>
      <span className="sub">колоды с косметикой</span>
      <span className="bp-home-chev" aria-hidden>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m9 5 7 7-7 7" />
        </svg>
      </span>
    </button>
  );
}
